import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, of } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { environment as env } from '../../../environments/environment';
import { LgvModel } from '../../data-models/longevity.model';

export interface CompanyStateLicense extends LgvModel.StateLicense {
  expirationYear: any;
  id: string;
  companyId: string;
  expirationDate: string;
  status: string;
  createdTimestamp: string;
  updatedTimestamp: string | null;
}



export interface StateLicensingService<T> {
  addStateLicense(entityId: string, license: LgvModel.StateLicense): Observable<T>;
  updateStateLicense(entityId: string, licenseId: string, license: LgvModel.UpdateStateLicenseRequest): Observable<T>;
  deleteStateLicense(entityId: string, licenseId: string): Observable<void>;
}

@Injectable({
  providedIn: 'root'
})
export class LgvCompanyStateLicensingService implements StateLicensingService<CompanyStateLicense> {
  constructor(private http: HttpClient) {}

  /**
   * Get all licenses for a specific company
   * @param companyId The ID of the company
   * @returns Observable of CompanyStateLicense array
   */
  getCompanyLicenses(companyId: string): Observable<CompanyStateLicense[]> {
    return this.http.get<{ value: CompanyStateLicense[] }>(`${env.LgvApiBaseUrl}/companies/${companyId}/stateLicenses`)
      .pipe(
        first(),
        map(response => {
          // If response is an array, return it directly
          if (Array.isArray(response)) {
            return response;
          }
          // Otherwise, return response.value or empty array
          return response?.value || [];
        }),
        catchError(error => {
          console.error('LgvCompanyLicensingService: Error getting licenses', error);
          return of([]);
        })
      );
  }

  /**
   * Add a new license for a specific company
   * @param companyId The ID of the company
   * @param license The license details to add
   * @returns Observable of the created CompanyStateLicense
   */
  addStateLicense(companyId: string, license: LgvModel.StateLicense): Observable<CompanyStateLicense> {
    return this.http.post<CompanyStateLicense>(`${env.LgvApiBaseUrl}/companies/${companyId}/stateLicenses`, license)
      .pipe(first());
  }

  /**
   * Update an existing license for a specific company
   * @param companyId The ID of the company
   * @param license The license details to update
   * @returns Observable of the updated CompanyStateLicense
   */
  updateStateLicense(companyId: string, licenseId: string, license: LgvModel.UpdateStateLicenseRequest): Observable<CompanyStateLicense> {
    return this.http.patch<CompanyStateLicense>(`${env.LgvApiBaseUrl}/companies/${companyId}/stateLicenses/${licenseId}`, license)
      .pipe(first());
  }

  /**
   * Delete a specific license for a company
   * @param companyId The ID of the company
   * @param licenseId The ID of the license to delete
   * @returns Observable of void
   */
  deleteStateLicense(companyId: string, licenseId: string): Observable<void> {
    return this.http.delete<void>(`${env.LgvApiBaseUrl}/companies/${companyId}/stateLicenses/${licenseId}`)
      .pipe(first());
  }
} 